import { Tooltip } from '@mui/material'
import { FC } from 'react'
import { useRecordContext, useTranslate } from 'react-admin'
import { StatusPill } from '../../components/ui'
import { StatusTone } from '../../layout/theme/color'
import { CertificateOut, CertificateStatus } from '../../types/apiRequests'

const STATUS_TONE: Record<CertificateStatus, StatusTone> = {
  [CertificateStatus.PENDING]: 'warning',
  [CertificateStatus.CSR_READY]: 'info',
  [CertificateStatus.CSR_DOWNLOADED]: 'info',
  [CertificateStatus.CERT_UPLOADED]: 'info',
  [CertificateStatus.INSTALL_SCHEDULED]: 'info',
  [CertificateStatus.ACTIVE]: 'success',
  [CertificateStatus.EXPIRED]: 'error',
  [CertificateStatus.ERROR]: 'error',
}

/** Status pill of the certificate in the record context. */
export const CertificateStatusPill: FC = () => {
  const record = useRecordContext<CertificateOut>()
  const translate = useTranslate()
  if (!record?.status) return null
  return (
    <StatusPill tone={STATUS_TONE[record.status]} pulse={record.status === CertificateStatus.ACTIVE}>
      {translate(`resources.certificates.status.${record.status}`, { _: record.status })}
    </StatusPill>
  )
}

const MS_PER_DAY = 1000 * 60 * 60 * 24
/** Within this many days an expiry needs attention. */
export const EXPIRY_WARNING_DAYS = 60

/** True from the exact expiry moment on (days would round a few hours past it to 0). */
export const isExpired = (record?: CertificateOut): boolean =>
  !!record?.verloopDatum && new Date(record.verloopDatum) < new Date()

/** Whole days until expiry (negative = expired), or null without an expiry date. */
export const daysUntilExpiry = (record?: CertificateOut): number | null => {
  if (!record?.verloopDatum) return null
  return Math.ceil((new Date(record.verloopDatum).getTime() - Date.now()) / MS_PER_DAY)
}

/** Expiry date as "21 oktober 2026 om 14:30" in the browser locale. */
export const formatExpiry = (record?: CertificateOut): string =>
  record?.verloopDatum
    ? new Date(record.verloopDatum).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' })
    : ''

/** Colour of an expiry: past, within the warning window, or fine. */
export const expiryTone = (days: number): StatusTone => {
  if (days < 0) return 'error'
  return days <= EXPIRY_WARNING_DAYS ? 'warning' : 'success'
}

/** "Nog 123 dagen" / "Verlopen" pill of the record in context, with the exact date as tooltip. */
export const CertificateValidityPill: FC = () => {
  const record = useRecordContext<CertificateOut>()
  const translate = useTranslate()
  const days = daysUntilExpiry(record)
  if (days === null) return null

  const date = formatExpiry(record)
  const label = days < 0
    ? translate('resources.certificates.validity.expired')
    // eslint-disable-next-line camelcase -- polyglot's pluralisation key
    : translate('resources.certificates.validity.daysLeft', { smart_count: days })
  const tooltip = days < 0
    ? translate('resources.certificates.tooltips.expiredOn', { date, _: `Verlopen op ${date}` })
    : translate('resources.certificates.tooltips.expiresOn', { date, _: `Verloopt op ${date}` })

  return (
    <Tooltip title={tooltip}>
      <span><StatusPill tone={expiryTone(days)}>{label}</StatusPill></span>
    </Tooltip>
  )
}
